/* External Deps */
import { FC, ReactElement, useMemo, useState } from "react";
import { Box, InputAdornment, Stack } from "@mui/material";

/* Internal Deps */
import { DynamicTable, IconImage, PageHeader } from "@/components";
import UploadDocument from "../../../upload-document";
import {
  HeaderRightNode,
  HeaderSearchBox,
  StyledContainedButton,
  StyledTextInput,
} from "../../../styles";
import { columns } from "@/columns/COMMON/documents";
import {
  useDeleteDocumentsMutation,
  useGetAllDocumentsQuery,
  usePostDocumentMutation,
} from "@/services";
import {
  useDebounce,
  useDownloadFile,
  useSearchQuery,
  useSnackbar,
} from "@/hooks";
import { search_img, upload_img } from "@/constants";
import { useAppSelector } from "@/store";
import { DocumentAccessLevel, roles } from "@/roles";

const EntrySummary: FC = (): ReactElement => {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [ids, setIds] = useState<{ delete: string[]; download: string[] }>({
    delete: [],
    download: [],
  });
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  const { show } = useSnackbar();
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);
  const { updateQuery, getQueryValue } = useSearchQuery();
  const { downloadFile } = useDownloadFile();
  const [postDocument, { isLoading: isPostDocumentLoading }] =
    usePostDocumentMutation();
  const [deleteDocument] = useDeleteDocumentsMutation();

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdmin = useMemo(() => {
    return user?.userRole === roles.TDADMIN;
  }, [user, selectedTenant]);

  const shouldSkip = useMemo(
    () => {
      if (isTDAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
        return false; // Allow query to run for TDADMIN with TradeDive Client, even if selectedClient is not set
      }
      return canSelectClients && !selectedClient;
    },
    [canSelectClients, selectedClient, selectedTenant]
  );

  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetAllDocumentsQuery(
    {
      searchKeyword: getQueryValue("search"),
      pageSize: limit,
      pageNo: page,
      clientId: selectedClient?._id,
      ...(isTDAdmin && { tenantId: selectedTenant?._id }),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkip,
    }
  );

  const canUploadDocs = useMemo(
    () =>
      user?.userRole !== roles.SPCLIENT ||
      user?.clientPermission?.documents === DocumentAccessLevel.Admin,
    [user]
  );

  const addDocument = async (
    docData: DocumentsDto["document"]
  ): Promise<void> => {
    const uploadedDocId = await postDocument({
      document: {
        ...docData,
      },
      ...(canSelectClients &&
        selectedClient && { clientId: selectedClient?._id }),
      ...(isTDAdmin && { tenantId: selectedTenant?._id }),
    }).unwrap();

    if (uploadedDocId) {
      show(
        "Successfully created!",
        `Document with id ${uploadedDocId} created sucessfully!`,
        "success"
      );
    }
  };

  const deleteDocumentAction = async (doc: DocDataTypes) => {
    const { _id } = doc;

    setIds({ delete: [_id], download: [...ids.download] });
    const isDeleted = await deleteDocument(_id).unwrap();
    setIds({ delete: [], download: [...ids.download] });

    if (isDeleted) {
      show(
        "Successfully deleted!",
        `Document with id ${_id} deleted sucessfully!`,
        "success"
      );
    }
  };

  const downloadDocumentFileAction = async (doc: DocDataTypes) => {
    setIds({ download: [doc._id], delete: [...ids.delete] });
    await downloadFile({ key: doc.fileUrl, name: doc.filename });
    setIds({ download: [], delete: [...ids.delete] });
  };

  const filterDocuments = useDebounce<string>((search) => {
    updateQuery({ search });
    setPage(1);
  }, 1200);

  const actions = {
    delete: deleteDocumentAction,
    download: downloadDocumentFileAction,
  };

  const handleActions = (type: "download" | "delete", doc: DocDataTypes) => {
    actions[type](doc);
  };

  const { data = [], meta } = apiData ?? {};

  const countDocs = meta?.countDocs ?? 0;

  return (
    <Stack sx={{ flex: 1 }}>
      <PageHeader
        title={"Entry Summary Documents"}
        rightNode={
          <HeaderRightNode gap={3}>
            <HeaderSearchBox>
              <StyledTextInput
                type="text"
                placeholder="Search"
                handleChangeValue={filterDocuments}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconImage src={search_img} size="auto" />
                    </InputAdornment>
                  ),
                }}
              />
            </HeaderSearchBox>
            {canUploadDocs && (
              <Box>
                <StyledContainedButton
                  startIcon={<IconImage src={upload_img} size="auto" />}
                  size="large"
                  title="Upload Document"
                  onClick={() => setShowUploadModal(true)}
                />
              </Box>
            )}
          </HeaderRightNode>
        }
      />

      <DynamicTable
        data={data}
        stickyHeader
        hideScrollBars={false}
        columns={columns({
          action: handleActions,
          actionsLoading: {
            ...ids,
          },
        })}
        sx={{
          flex: 1,
          "& .MuiTableContainer-root": { '&::-webkit-scrollbar': { height: 6, width: 6 } },
          "& .MuiTable-root": {
            borderCollapse: "collapse",
            tableLayout: 'fixed',
            width: '100%',
            minWidth: { xs: 800, md: '100%' },
            "& .MuiTableCell-root": {
              fontSize: { xs: '11px', md: '12px' },
              padding: { xs: '6px 8px', md: '8px 10px' },
            },
          },
          "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
            borderBottom: "1px solid #e6e6e9 !important",
          },
        }}
        isLoading={shouldSkip || isLoading || isFetching}
        handlePageChange={setPage}
        handleLimitChange={setLimit}
        limit={limit}
        page={page}
        total={countDocs}
        hidePagination={false}
      />

      <UploadDocument
        title="Upload Document"
        subTitle="Please upload your entry documents. Our system will automatically pull the key details needed for processing."
        show={showUploadModal}
        setShow={setShowUploadModal}
        buttonText={isPostDocumentLoading ? "Loading" : "Confirm"}
        buttonAction={addDocument}
        isLoading={isPostDocumentLoading}
      />
    </Stack>
  );
};

export default EntrySummary;